import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Checkbox, Tooltip } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { get, set } from '@totalsoft/change-tracking-react';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { seasonalityTypeIds } from '../../constants';
import { over } from '@totalsoft/change-tracking-react';
import { repeat } from 'ramda';

const defaultDetail = {
  ScheduleTypeCellParamTemplateDetailId: null,
  scheduleTypeCellParamTemplateId: null
};

export const ScheduleTypeCellParamTemplateHeaderComponent = ({
  scheduleTypeCellParamTemplateLens,
  scheduleTypes,
  validation,
  canWrite
}) => {
  const [t] = useTranslation();
  const scheduleTypeCellParamTemplate = scheduleTypeCellParamTemplateLens |> get;
  const seasonalityTypeOptions = useMemo(
    () => [
      { seasonalityTypeId: seasonalityTypeIds.Seasonal, seasonalityTypeName: t('ScheduleTypeCellParamTemplate.Seasonal') },
      { seasonalityTypeId: seasonalityTypeIds.Monthly, seasonalityTypeName: t('ScheduleTypeCellParamTemplate.Monthly') }
    ],
    [t]
  );

  const onChangeMonthsNo = useCallback(
    event => {
      return over(scheduleTypeCellParamTemplateLens, prevValue => ({
        ...prevValue,
        monthsNo:
          prevValue.seasonalityTypeId === seasonalityTypeIds.Monthly || Object.keys(prevValue).length === 0 ? event : prevValue.monthsNo,
        scheduleTypeCellParamTemplateDetails:
          prevValue.seasonalityTypeId === seasonalityTypeIds.Monthly
            ? event > prevValue.scheduleTypeCellParamTemplateDetails.length
              ? prevValue.scheduleTypeCellParamTemplateDetails
                  .concat(repeat(defaultDetail, event - prevValue.scheduleTypeCellParamTemplateDetails.length))
                  .map((x, i) => ({ ...x, MonthNo: i + 1 }))
              : prevValue.scheduleTypeCellParamTemplateDetails.slice(0, event)
            : prevValue.scheduleTypeCellParamTemplateDetails
      }));
    },
    [scheduleTypeCellParamTemplateLens]
  );

  const onChangeSeasonalityType = useCallback(
    event =>
      over(scheduleTypeCellParamTemplateLens, prevValue => {
        return {
          ...prevValue,
          seasonalityTypeId: event.seasonalityTypeId,
          monthsNo: 12,
          scheduleTypeCellParamTemplateDetails: repeat(defaultDetail, 12).map((x, i) => ({ ...x, MonthNo: i + 1 }))
        };
      }),
    [scheduleTypeCellParamTemplateLens]
  );

  const isCalendarView = (scheduleTypeCellParamTemplateLens.seasonalityTypeId |> get) === seasonalityTypeIds.Seasonal;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <TextField
          required
          fullWidth
          value={scheduleTypeCellParamTemplate.templateName}
          onChange={scheduleTypeCellParamTemplateLens.templateName |> set}
          label={t('ScheduleTypeCellParamTemplate.TemplateName')}
          error={!isValid(validation.templateName)}
          helperText={getErrors(validation.templateName)}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Tooltip title={t('ScheduleTypeCellParamTemplate.ScheduleTypeTooltip')} placement='top'>
          <Autocomplete
            required
            fullWidth
            labelKey='scheduleTypeName'
            label={t('ScheduleTypeCellParamTemplate.ScheduleTypeName')}
            valueKey='scheduleTypeId'
            value={{
              scheduleTypeId: scheduleTypeCellParamTemplate.scheduleTypeId,
              scheduleTypeName: scheduleTypes.find(x => x.scheduleTypeId === scheduleTypeCellParamTemplate.scheduleTypeId)?.scheduleTypeName
            }}
            options={scheduleTypes}
            onChange={event => {
              if (window.confirm(t('ScheduleTypeCellParamTemplate.ThisActionWillDelete'))) {
                over(scheduleTypeCellParamTemplateLens, prevValue => {
                  return {
                    ...prevValue,
                    scheduleTypeId: event.scheduleTypeId,
                    seasonalityTypeId: event.seasonalityTypeId,
                    monthsNo: 12,
                    scheduleTypeCellParamTemplateDetails: repeat(defaultDetail,12).map((x, i) => ({ ...x, MonthNo: i + 1 }))
                  };
                });
              }
            }}
            error={!isValid(validation.scheduleTypeId)}
            helperText={getErrors(validation.scheduleTypeId)}
            disabled={!canWrite}
          />
        </Tooltip>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          required
          fullWidth
          labelKey='seasonalityTypeName'
          label={t('ScheduleTypeCellParamTemplate.SeasonalityType')}
          valueKey='seasonalityTypeId'
          value={{
            seasonalityTypeId: scheduleTypeCellParamTemplate.seasonalityTypeId,
            seasonalityTypeName: seasonalityTypeOptions.find(x => x.seasonalityTypeId === scheduleTypeCellParamTemplate.seasonalityTypeId)
              ?.seasonalityTypeName
          }}
          options={seasonalityTypeOptions}
          onChange={event => {
            onChangeSeasonalityType(event);
          }}
          error={!isValid(validation.seasonalityTypeId)}
          helperText={getErrors(validation.seasonalityTypeId)}
          disabled={!canWrite}
        />
      </Grid>
      {!isCalendarView ? (
        <Grid item xs={12} sm={4} lg={3}>
          <TextField
            isNumeric
            isStepper={canWrite}
            fullWidth
            label={t('ScheduleTypeCellParamTemplate.MonthsNo')}
            value={scheduleTypeCellParamTemplate.monthsNo || ''}
            inputProps={{ decimalScale: 0 }}
            onChange={onChangeMonthsNo}
            disabled={scheduleTypeCellParamTemplate.seasonalityTypeId === seasonalityTypeIds.Seasonal || !canWrite}
            error={!isValid(validation.monthsNo)}
            helperText={getErrors(validation.monthsNo)}
          />
        </Grid>
      ) : (
        <Grid item xs={12} sm={4} lg={3}></Grid>
      )}
      <Grid item container xs={12} sm={8} lg={9} float='right'>
        <Grid item xs={6}>
          <FormControlLabel
            style={{
              display: 'flex',
              float: 'right'
            }}
            control={
              <Checkbox
                checked={scheduleTypeCellParamTemplate.activeYN === true}
                onChange={scheduleTypeCellParamTemplateLens.activeYN |> set |> onCheckBoxChange}
                disabled={!canWrite}
              />
            }
            label={t('ScheduleTypeCellParamTemplate.ActiveYN')}
            labelPlacement='end'
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            style={{
              display: 'flex',
              float: 'right'
            }}
            control={
              <Checkbox
                checked={scheduleTypeCellParamTemplate.isEditable === true}
                onChange={scheduleTypeCellParamTemplateLens.isEditable |> set |> onCheckBoxChange}
                disabled={!canWrite}
              />
            }
            label={t('ScheduleTypeCellParamTemplate.IsEditable')}
            labelPlacement='end'
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

ScheduleTypeCellParamTemplateHeaderComponent.propTypes = {
  scheduleTypeCellParamTemplateLens: PropTypes.object.isRequired,
  scheduleTypes: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired,
  canWrite: PropTypes.bool.isRequired
};

export default ScheduleTypeCellParamTemplateHeaderComponent;
